/**
 * PROVISIONING-FLOW-FRONTEND-1 — helpers PUROS do fluxo de provisionamento do
 * InstallerPortal (Cliente × Infraestrutura/Torre). Extraídos p/ teste em node.
 */

// INSTALLER-TEMPLATES-UX-2 — opções da etapa Template, NA ORDEM de exibição.
// "custom" (Sem template / Configurar depois) é o primeiro e o padrão seguro.
// Valores internos inalterados: custom | residential | business.
export const INSTALLER_TEMPLATE_OPTIONS = [
  {
    id: "custom",
    name: "Sem template / Configurar depois",
    desc: "Não aplica nenhuma configuração automática agora. O equipamento poderá ser configurado depois pelo NOC.",
    badge: "Padrão seguro",
    recommended: true,
  },
  {
    id: "residential",
    name: "Residencial",
    desc: "Aplica watchdog padrão para cliente residencial e reboot preventivo 2×/semana (ter+sex).",
    badge: "Aplica comandos automáticos",
  },
  {
    id: "business",
    name: "Empresarial",
    desc: "Aplica watchdog conservador em modo alerta, sem reboot automático do relé.",
    badge: "Sem reboot automático",
  },
];

/** Padrão seguro inicial da etapa Template. */
export const DEFAULT_INSTALLER_TEMPLATE = "custom";

/** Rótulo + nota para o resumo final. custom (ou ausente) deixa claro que nada
 *  automático é aplicado. */
export function templateSummary(templateId) {
  if (!templateId || templateId === "custom") {
    return {
      label: "Sem template / Configurar depois",
      note: "Nenhuma configuração automática será aplicada.",
    };
  }
  const opt = INSTALLER_TEMPLATE_OPTIONS.find((o) => o.id === templateId);
  return { label: opt ? opt.name : templateId, note: null };
}

// INSTALLER-SCHEDULE-PRESETS-2E — fallback local do catálogo de agenda.
// A fonte da verdade é GET /api/installer/templates (schedule_presets[]);
// este espelho só entra se o fetch falhar (ids são valores de produto
// estáveis, iguais ao catálogo server-side de initial_templates.py).
export const FALLBACK_SCHEDULE_PRESETS = [
  { id: null, group: "provider_default", window: null,
    label: "Padrão do provedor",
    description: "Usa a agenda definida pelo NOC para este tipo de instalação.",
    is_default: true },
  { id: "none", group: "none", window: null, label: "Sem agenda",
    description: "Nenhum reboot automático agendado.", is_default: false },
  { id: "tue_fri_0300", group: "tue_fri", window: "03:00–03:58",
    label: "Ter/sex — janela 03:00–03:58",
    description: "Reboot preventivo terça e sexta, janela das 3h.", is_default: false },
  { id: "tue_fri_0400", group: "tue_fri", window: "04:00–04:58",
    label: "Ter/sex — janela 04:00–04:58",
    description: "Reboot preventivo terça e sexta, janela das 4h.", is_default: false },
  { id: "tue_fri_0500", group: "tue_fri", window: "05:00–05:58",
    label: "Ter/sex — janela 05:00–05:58",
    description: "Reboot preventivo terça e sexta, janela das 5h.", is_default: false },
  { id: "daily_0400", group: "daily", window: "04:00–04:58",
    label: "Diário — janela 04:00–04:58",
    description: "Reboot preventivo todo dia de madrugada.", is_default: false },
  { id: "mwf_0400", group: "mwf", window: "04:00–04:58",
    label: "Seg/qua/sex — janela 04:00–04:58",
    description: "Reboot preventivo 3x por semana de madrugada.", is_default: false },
  { id: "sun_0400", group: "sun", window: "04:00–04:58",
    label: "Domingo — janela 04:00–04:58",
    description: "Reboot preventivo aos domingos de madrugada.", is_default: false },
];

// ── INSTALLER-SCHEDULE-PRESETS-3B — agrupamento por frequência + janelas ──

/** Agrupa o catálogo em CARDS por frequência. Grupos com >1 opção (ter+sex)
 *  ganham seletor de janela; o rótulo do card perde o sufixo " — janela …".
 *  Puro e defensivo: sem `group` (backend antigo) cada preset vira card. */
export function groupSchedulePresets(presets) {
  const list = presets && presets.length ? presets : FALLBACK_SCHEDULE_PRESETS;
  const cards = [];
  const byGroup = new Map();
  for (const p of list) {
    const g = p.group || (p.id == null ? "provider_default" : p.id);
    if (!byGroup.has(g)) {
      const card = {
        group: g,
        label: p.label,
        description: p.description || null,
        is_default: !!p.is_default,
        options: [],
      };
      byGroup.set(g, card);
      cards.push(card);
    }
    byGroup.get(g).options.push({
      id: p.id, window: p.window || null, label: p.label,
    });
  }
  for (const c of cards) {
    if (c.options.length > 1) {
      c.label = String(c.label).split(" — ")[0];
      c.description = "Escolha a janela de horário abaixo.";
    }
  }
  return cards;
}

/** Id enviado ao tocar num card: opção única → ela; múltiplas → janela 04h
 *  (padrão do provedor) ou a 1ª. */
export function defaultOptionId(card) {
  if (!card || !card.options || !card.options.length) return null;
  if (card.options.length === 1) return card.options[0].id;
  const pref = card.options.find((o) => (o.window || "").startsWith("04"));
  return (pref || card.options[0]).id;
}

/** Rótulo curto da janela p/ o seletor segmentado: "03:00–03:58" → "03h". */
export function windowShort(window) {
  const m = /^(\d{2})/.exec(window || "");
  return m ? `${m[1]}h` : window || "—";
}

/** Linha "Agenda" do resumo Finalizar. Lê o preset PERSISTIDO na sessão
 *  (raw_json.provisioning.schedule_preset) — nunca o estado local. */
export function schedulePresetSummary(presetId, templateId, presets) {
  if (!templateId || templateId === "custom") {
    return { label: "—", note: "Sem template — nenhuma agenda automática." };
  }
  if (!presetId) {
    return {
      label: "Padrão do provedor",
      note: templateId === "business"
        ? "Padrão empresarial: sem reboot automático."
        : "Padrão residencial: reboot preventivo 2×/semana (ter+sex), de madrugada.",
    };
  }
  const list = presets && presets.length ? presets : FALLBACK_SCHEDULE_PRESETS;
  const opt = list.find((o) => o.id === presetId);
  if (presetId === "none") return { label: "Sem agenda", note: null };
  return { label: opt ? opt.label : presetId, note: null };
}

/** Passos dinâmicos do wizard por tipo. Infra NÃO passa por Cliente/Template
 *  (sem IXC). */
export function stepsFor(deploymentType) {
  return deploymentType === "infrastructure"
    ? ["equipment", "type", "infra", "finish"]
    : ["equipment", "type", "customer", "template", "finish"];
}

/** Detecta o IXC restrito: 401 (sem auth) OU 403 (autenticado mas sem permissão,
 *  ex.: técnico field — IXC é platform-only). Mensagem amigável, sem reabrir IXC. */
export function isIxcProtected(err) {
  if (!err) return false;
  if (err.status === 401 || err.status === 403) return true;
  return /authentication required|protegid|unauthor|forbidden|permiss/i.test(
    String(err.message || "")
  );
}

/** Mensagem amigável p/ o IXC restrito (FIELD-IXC-AUTH-BRIDGE-1):
 *  - NÃO logado (401): precisa logar como técnico;
 *  - logado mas SEM permissão (403, ex.: viewer): mensagem amigável de permissão.
 *  Usuário logado e permitido (field/noc/tenant_admin/super_admin) recebe 200 e
 *  esta mensagem não aparece. */
export function ixcRestrictedMessage(loggedIn) {
  return loggedIn
    ? "Sua conta não tem permissão para buscar clientes IXC. Use Infraestrutura/Torre ou peça liberação via NOC."
    : "Faça login como técnico para buscar clientes IXC, ou use o fluxo de Infraestrutura/Torre.";
}

/** Monta o bloco `provisioning` da Infra para gravar em
 *  installation_sessions.raw_json.provisioning. Coordenada só vai se válida;
 *  origem 'manual' quando o técnico edita lat/lng à mão. */
export function buildInfraProvisioning({
  operationalIdentifier,
  hardwareCapability,
  category,
  displayName,
  notes,
  lat,
  lng,
  accuracyM,
  locationSource,
} = {}) {
  const hasCoord =
    lat !== "" && lat != null && lng !== "" && lng != null &&
    !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));
  return {
    deployment_type: "infrastructure",
    hardware_capability: hardwareCapability || "core",
    operational_identifier: (operationalIdentifier || "").trim(),
    category: category || null,
    display_name: (displayName || "").trim() || null,
    notes: (notes || "").trim() || null,
    location: hasCoord
      ? {
          lat: Number(lat),
          lng: Number(lng),
          accuracy_m: accuracyM != null ? Number(accuracyM) : null,
          location_source: locationSource || "manual",
        }
      : null,
  };
}
